import {
  ArrowBack as BackIcon,
  Close as CloseIcon,
  InfoOutlined as InfoIcon,
  PlayArrow as StartIcon,
} from '@mui/icons-material';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import type React from 'react';
import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import SourceMetaForm, {
  EMPTY_SOURCE_META,
  isSourceMetaComplete,
  type SourceMeta,
} from '../components/upload/SourceMetaForm';
import { useAuth } from '../contexts/AuthContext';
import { useThemeMode } from '../contexts/ThemeContext';
import { useThemeColors } from '../hooks/useThemeColors';
import { uploadPdfAPI } from '../services/api';
import { createFactCheckItem } from '../services/factCheckItemService';
import { IMG } from '../theme/assets';

const CreateItem: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { themeMode } = useThemeMode();
  const tokens = useThemeColors();

  // アイテム情報
  // 資料メタ情報（抽出プロンプトに注入し、主張の文脈補完に使う）
  const [sourceMeta, setSourceMeta] = useState<SourceMeta>(EMPTY_SOURCE_META);

  // ファイルアップロード
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 受け付ける拡張子（バックエンドの supported_extensions と揃える）
  const ACCEPTED_EXTENSIONS = ['.pdf', '.xlsx', '.xlsm', '.docx', '.pptx'];

  // 案件ID（案件からの作成時に使用）
  const workspaceId = (location.state as any)?.workspaceId || null;

  const handleFileSelect = (file: File) => {
    // MIME type はOSやブラウザで揺れるため拡張子で判定する
    if (!ACCEPTED_EXTENSIONS.some((ext) => file.name.toLowerCase().endsWith(ext))) {
      setError('PDF・Excel・Word・PowerPoint のファイルをアップロードしてください。');
      return;
    }
    setSelectedFile(file);
    // 資料の名称が未入力ならファイル名で補完する（そのまま編集できる）
    if (!sourceMeta.title.trim()) {
      setSourceMeta((m) => ({ ...m, title: file.name.replace(/\.[^/.]+$/, '') }));
    }
    setError(null);
  };

  const handleClearFile = () => {
    setSelectedFile(null);
    setError(null);
  };

  const handleStartProcessing = async () => {
    if (!selectedFile || !user) {
      setError('ファイルを選択してください。');
      return;
    }

    setUploading(true);
    setError(null);

    try {
      // 動画フローと同様に、まず factcheck_item を作成して id を確定させ、
      // その id をアップロードに引き渡す。これによりバックエンドが
      // 進捗・抽出結果を直接アイテムへ反映でき、画面を離れても確定する。
      const item = await createFactCheckItem({
        workspaceId: workspaceId, // nullなら個人アイテム、指定があれば案件アイテム
        type: 'document',
        // 表示用のタイトル・詳細は資料の名称・概要をそのまま使う（入力を二重にしない）
        title: sourceMeta.title,
        description: sourceMeta.summary,
        fileName: selectedFile.name,
        status: 'extracting',
        sourceMeta,
      });

      const response = await uploadPdfAPI(selectedFile, item.id, sourceMeta);

      if (response.success && response.data) {
        // スキャンPDF: 非同期OCR処理中。進捗画面へ遷移してポーリングする
        if (response.data.status === 'processing') {
          console.log('🖼️ スキャンPDF: 非同期OCR処理を開始', item.id);

          navigate(`/document/processing/${item.id}`, {
            state: {
              itemId: item.id,
              workspaceId,
              pdfUrl: response.data.pdf_url,
              filename: selectedFile.name,
              itemTitle: sourceMeta.title,
              itemDescription: sourceMeta.summary,
            },
          });
          return;
        }

        // テキストPDF: バックエンドがアイテムにファクトを反映済み。確認画面へ
        console.log('✅ アイテム保存完了:', item);

        navigate(`/workflow/${item.id}`, {
          state: {
            extractedFacts: response.data.facts,
            pdfUrl: response.data.pdf_url,
            filename: selectedFile.name,
            itemTitle: sourceMeta.title,
            itemDescription: sourceMeta.summary,
          },
        });
      } else {
        setError(response.error || 'アップロードに失敗しました。');
      }
    } catch (err) {
      console.error('アップロードエラー:', err);
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setError(`アップロード中にエラーが発生しました: ${errorMessage}`);
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  // 戻り先を決定
  const handleGoBack = () => {
    if (workspaceId) {
      navigate(`/share/${workspaceId}`);
    } else {
      navigate('/');
    }
  };

  const isDark = themeMode === 'dark';
  const canStart = !!selectedFile && isSourceMetaComplete(sourceMeta) && !uploading;

  // フォーム入力共通スタイル（mock 準拠: 角丸・teal フォーカスリング）
  return (
    <Box sx={{ maxWidth: 1040, mx: 'auto' }}>
      {/* ヘッダー（戻るピル + タイトル） */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2.25 }}>
        <Button
          onClick={handleGoBack}
          startIcon={<BackIcon sx={{ fontSize: 16 }} />}
          disableElevation
          sx={{
            background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,.9)',
            border: `1px solid ${tokens.line}`,
            borderRadius: '11px',
            px: 1.75,
            py: 1,
            fontSize: 13,
            fontWeight: 700,
            color: tokens.ink,
            textTransform: 'none',
            '&:hover': {
              background: isDark ? 'rgba(255,255,255,0.1)' : '#fff',
            },
          }}
        >
          {workspaceId ? '案件に戻る' : 'マイページに戻る'}
        </Button>
        <Typography sx={{ fontSize: 21, fontWeight: 900, color: tokens.ink }}>
          新規文書を読み込ませる
        </Typography>
      </Box>

      {/* ヒーローバナー */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          background: `linear-gradient(120deg, ${tokens.heroFrom}, ${tokens.heroTo})`,
          border: `1px solid ${tokens.line}`,
          borderRadius: '13px',
          px: 2.75,
          py: 2,
          mb: 2.25,
          boxShadow: '0 8px 24px rgba(30,30,70,.06)',
        }}
      >
        <Box
          component="img"
          src={IMG.owl.clipboard}
          alt=""
          sx={{
            width: 62,
            height: 62,
            objectFit: 'contain',
            flexShrink: 0,
            filter: 'drop-shadow(0 6px 12px rgba(30,30,70,.16))',
          }}
        />
        <Box>
          <Typography sx={{ fontSize: 18, fontWeight: 900, color: tokens.ink }}>
            検証する資料を取り込みましょう。
          </Typography>
          <Typography sx={{ fontSize: 12.5, color: tokens.muted, mt: 0.5, lineHeight: 1.5 }}>
            PDF・Excel・Word・PowerPoint の資料から、AIが主張を抽出して検証します。
          </Typography>
        </Box>
      </Box>

      {/* エラー表示 */}
      {error && (
        <Box
          role="alert"
          sx={{
            display: 'flex',
            gap: 1.25,
            alignItems: 'flex-start',
            background: tokens.verdict.inaccurate.tint,
            border: `1px solid ${isDark ? 'rgba(248,113,113,0.35)' : '#f5c9c9'}`,
            color: tokens.verdict.inaccurate.color,
            borderRadius: '12px',
            px: 1.875,
            py: 1.5,
            mb: 2.25,
            fontSize: 13,
            lineHeight: 1.5,
          }}
        >
          {error}
        </Box>
      )}

      {/* 2カラム: アイテム情報 / ファイルアップロード */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1.05fr' },
          gap: 2.5,
          alignItems: 'start',
        }}
      >
        {/* Panel 1: アイテム情報 */}
        <Box
          sx={{
            background: tokens.paper,
            border: `1px solid ${tokens.line}`,
            borderRadius: '13px',
            p: '20px 22px',
            boxShadow: '0 8px 24px rgba(30,30,70,.06)',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Box
              sx={{
                width: 22,
                height: 22,
                borderRadius: '50%',
                background: tokens.teal,
                color: '#fff',
                fontSize: 12,
                fontWeight: 900,
                display: 'grid',
                placeItems: 'center',
                flexShrink: 0,
              }}
            >
              1
            </Box>
            <Typography sx={{ fontSize: 13, fontWeight: 900, color: tokens.ink }}>
              アイテム情報
            </Typography>
          </Box>

          <Box sx={{ mb: 2 }}>
            <SourceMetaForm value={sourceMeta} onChange={setSourceMeta} disabled={uploading} />
          </Box>

          {/* 注記 */}
          <Box
            sx={{
              display: 'flex',
              gap: 1.125,
              alignItems: 'flex-start',
              background: isDark ? 'rgba(129,140,248,0.12)' : '#eef5ff',
              border: `1px solid ${isDark ? 'rgba(129,140,248,0.3)' : '#d9e2ff'}`,
              borderRadius: '12px',
              px: 1.625,
              py: 1.375,
              fontSize: 11.5,
              color: isDark ? tokens.muted : '#3f4a6b',
              lineHeight: 1.55,
            }}
          >
            <InfoIcon sx={{ fontSize: 16, color: tokens.indigo, flexShrink: 0, mt: '1px' }} />
            <span>取り込み後、抽出された主張を確認・編集してから検証を開始できます。</span>
          </Box>
        </Box>

        {/* Panel 2: ファイルアップロード */}
        <Box
          sx={{
            background: tokens.paper,
            border: `1px solid ${tokens.line}`,
            borderRadius: '13px',
            p: '20px 22px',
            boxShadow: '0 8px 24px rgba(30,30,70,.06)',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Box
              sx={{
                width: 22,
                height: 22,
                borderRadius: '50%',
                background: tokens.teal,
                color: '#fff',
                fontSize: 12,
                fontWeight: 900,
                display: 'grid',
                placeItems: 'center',
                flexShrink: 0,
              }}
            >
              2
            </Box>
            <Typography sx={{ fontSize: 13, fontWeight: 900, color: tokens.ink }}>
              ファイルアップロード
            </Typography>
          </Box>

          <input
            type="file"
            accept=".pdf,.xlsx,.xlsm,.docx,.pptx"
            onChange={handleFileInput}
            style={{ display: 'none' }}
            id="file-input"
            disabled={uploading}
          />

          {selectedFile ? (
            /* 選択済みファイル表示 */
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.625,
                background: isDark
                  ? 'linear-gradient(180deg, rgba(45,212,191,0.1), rgba(255,255,255,0.02))'
                  : 'linear-gradient(180deg, #f1fbf8, #fff)',
                border: `1.5px solid ${isDark ? 'rgba(45,212,191,0.4)' : 'rgba(13,148,136,.35)'}`,
                borderRadius: '14px',
                px: 2,
                py: 1.75,
              }}
            >
              <Box
                component="img"
                src={IMG.create.pdf}
                alt=""
                sx={{ width: 44, height: 44, objectFit: 'contain', flexShrink: 0 }}
              />
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  sx={{
                    fontSize: 13.5,
                    fontWeight: 800,
                    color: tokens.ink,
                    wordBreak: 'break-all',
                  }}
                >
                  {selectedFile.name}
                </Typography>
                <Typography sx={{ fontSize: 11.5, color: tokens.muted, mt: 0.25 }}>
                  {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                </Typography>
              </Box>
              {!uploading && (
                <Button
                  onClick={handleClearFile}
                  aria-label="削除"
                  sx={{
                    ml: 'auto',
                    minWidth: 0,
                    width: 30,
                    height: 30,
                    p: 0,
                    background: isDark ? 'rgba(255,255,255,0.06)' : '#fff',
                    border: `1px solid ${tokens.line}`,
                    borderRadius: '9px',
                    color: tokens.faint,
                    flexShrink: 0,
                    '&:hover': {
                      color: tokens.verdict.inaccurate.color,
                      borderColor: tokens.verdict.inaccurate.color,
                      background: isDark ? 'rgba(255,255,255,0.06)' : '#fff',
                    },
                  }}
                >
                  <CloseIcon sx={{ fontSize: 16 }} />
                </Button>
              )}
            </Box>
          ) : (
            /* ドロップゾーン */
            <Box
              component="label"
              htmlFor="file-input"
              onDragOver={(e) => {
                e.preventDefault();
                if (!uploading) setDragOver(true);
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 1.5,
                border: `2px ${dragOver ? 'solid' : 'dashed'} ${
                  dragOver ? tokens.teal : isDark ? 'rgba(255,255,255,0.18)' : '#c4cad8'
                }`,
                borderRadius: '12px',
                background: dragOver
                  ? isDark
                    ? 'rgba(45,212,191,0.1)'
                    : '#eafaf6'
                  : isDark
                    ? 'rgba(255,255,255,0.02)'
                    : 'rgba(255,255,255,.5)',
                p: '34px 20px',
                textAlign: 'center',
                cursor: uploading ? 'not-allowed' : 'pointer',
                transition: '.15s',
                '&:hover': uploading
                  ? {}
                  : {
                      borderColor: tokens.teal,
                      background: isDark ? 'rgba(45,212,191,0.08)' : '#f1fbf8',
                    },
              }}
            >
              <Box
                component="img"
                src={IMG.create.upload}
                alt=""
                sx={{
                  width: 76,
                  height: 76,
                  objectFit: 'contain',
                  filter: 'drop-shadow(0 6px 12px rgba(30,30,70,.14))',
                }}
              />
              <Typography sx={{ fontSize: 14, fontWeight: 900, color: tokens.ink }}>
                ここに資料を
                <Box component="b" sx={{ color: tokens.teal }}>
                  ドラッグ＆ドロップ
                </Box>
              </Typography>
              <Typography sx={{ fontSize: 11.5, color: tokens.muted, lineHeight: 1.5 }}>
                または クリックしてファイルを選択
              </Typography>
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.75,
                  background: isDark ? 'rgba(255,255,255,0.06)' : '#eef1f5',
                  borderRadius: 999,
                  px: 1.5,
                  py: 0.5,
                  fontSize: 11,
                  fontWeight: 800,
                  color: tokens.muted,
                }}
              >
                対応形式: PDF・Excel・Word・PowerPoint
              </Box>
            </Box>
          )}
        </Box>
      </Box>

      {/* CTA */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75, mt: 2.5 }}>
        <Box sx={{ flex: 1 }} />
        <Button
          onClick={handleStartProcessing}
          disabled={!canStart}
          startIcon={<StartIcon sx={{ fontSize: 17 }} />}
          disableElevation
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 1.125,
            background: 'linear-gradient(120deg, #0d9488, #5b56e0)',
            color: '#fff',
            fontWeight: 800,
            fontSize: 14.5,
            borderRadius: '13px',
            px: 3,
            py: 1.625,
            textTransform: 'none',
            boxShadow: '0 10px 24px rgba(40,50,90,.22)',
            transition: '.15s',
            '&:hover': {
              background: 'linear-gradient(120deg, #0c8579, #524ed1)',
              boxShadow: '0 12px 28px rgba(40,50,90,.28)',
            },
            '&.Mui-disabled': {
              background: isDark ? 'rgba(255,255,255,0.12)' : '#cbd2e0',
              color: isDark ? 'rgba(255,255,255,0.4)' : '#fff',
              boxShadow: 'none',
            },
          }}
        >
          {uploading ? 'ファクト抽出中...' : 'ファクトチェック開始'}
        </Button>
      </Box>

      {/* 処理中オーバーレイ */}
      {uploading && (
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            zIndex: 1300,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(20,24,40,.45)',
            backdropFilter: 'blur(3px)',
          }}
        >
          <Box
            sx={{
              background: isDark ? '#1c1f29' : '#fff',
              borderRadius: '14px',
              p: '26px 34px',
              boxShadow: '0 24px 60px rgba(0,0,0,.3)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 1.75,
              maxWidth: 340,
              textAlign: 'center',
            }}
          >
            <CircularProgress size={44} thickness={5} sx={{ color: tokens.teal }} />
            <Typography sx={{ fontSize: 15, fontWeight: 900, color: tokens.ink }}>
              ファクトを抽出しています…
            </Typography>
            <Typography sx={{ fontSize: 12, color: tokens.muted, lineHeight: 1.5 }}>
              資料を解析しています。この画面を閉じても処理は続き、 結果は一覧から確認できます。
            </Typography>
            <Button
              onClick={handleGoBack}
              sx={{
                mt: 0.5,
                fontSize: 12.5,
                fontWeight: 800,
                color: tokens.muted,
                textTransform: 'none',
                '&:hover': { background: 'transparent', color: tokens.ink },
              }}
            >
              一覧に戻る
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default CreateItem;
